import { AxiosResponse } from "axios";
import TmbAction from "../models/tmb/TmbAction";
import Action from "../models/test-series/Action";
import Parameter from "../models/test-series/Parameter";
import ParameterValue from "../models/test-series/ParameterValue";
import TmbParameter from "../models/tmb/TmbParameter";
import NumberParameter from "../models/test-series/NumberParameter";
import NumberParameterValue from "../models/test-series/NumberParameterValue";
import TmbNumberParameter from "../models/tmb/TmbNumberParameter";
import TmbEnumParameter from "../models/tmb/TmbEnumParameter";
import TextParameterValue from "../models/test-series/TextParameterValue";
import EnumParameter from "../models/test-series/EnumParameter";
import EnumParameterValue from "../models/test-series/EnumParameterValue";
import PagedResponse from "../models/PagedResponse";
import TmbSubstrateClass from "../models/tmb/TmbSubstrateClass";
import SubstrateClass from "../models/substrate/SubstrateClass";
import { Tree } from "../../pages/Requests/SubstrateTab/CustomTreeView";
import TmbSubstrateClassBase from "../models/tmb/TmbSubstrateClassBase";
import Api from "./Api";
import { ConfirmationDialogContextType } from "../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import TmbActionTemplate from "../models/tmb/TmbActionTemplate";
import TmbParameterValue from "../models/tmb/TmbParameterValue";
import ActionValue from "../models/test-series/ActionValue";
import TmbActionValue from "../models/tmb/TmbActionValue";
import ActionTemplate from "../models/test-series/ActionTemplate";
import TmbActionValueRow from "../models/tmb/TmbActionValueRow";
import ActionValueTableRow from "../models/test-series/ActionValueTableRow";
import TmbTestPlanVersionResult from "../models/tmb/TmbTestPlanVersionResult";
import TmbTestPlan from "../models/tmb/TmbTestPlan";
import TestSpecificationLifecycleReference from "../models/tmb/TestSpecificationLifecycleReference";
import LifeCycleStateByVersion from "../models/tmb/LifecycleStateByVersion";
import ApiResponse from "../models/ApiResponse";
import TmbTestPlanVersion from "../models/tmb/TmbTestPlanVersion";

function mapNumberParameter(parameter: TmbNumberParameter): NumberParameter {
  return {
    name: parameter.name,
    type: parameter.type,
    tmbId: parameter.id,
    unit: {
      scalings: parameter.unit.scaling,
      id: parameter.unit.id,
      type: parameter.unit.type,
    },
  };
}

function mapEnumParameter(parameter: TmbEnumParameter): EnumParameter {
  return {
    name: parameter.name,
    type: parameter.type,
    tmbId: parameter.id,
    values: parameter.values,
  };
}

function mapDefaultParameter(parameter: TmbParameter): Parameter {
  return {
    name: parameter.name,
    type: parameter.type,
    tmbId: parameter.id,
  };
}

export function mapParameter(parameter: TmbParameter): Parameter {
  if (parameter.type === "NUMBER") {
    return mapNumberParameter(parameter as TmbNumberParameter);
  }
  if (parameter.type === "ENUM") {
    return mapEnumParameter(parameter as TmbEnumParameter);
  }
  return mapDefaultParameter(parameter);
}

export function mapNumberParameterValue(tmbParameterValue: TmbParameterValue): NumberParameterValue {
  return {
    value: tmbParameterValue.value as number,
    type: tmbParameterValue.type,
    unitCode: tmbParameterValue.unitCode,
    parameter: mapParameter(tmbParameterValue.parameter) as NumberParameter,
  };
}

export function mapEnumParameterValue(tmbParameterValue: TmbParameterValue): EnumParameterValue {
  return {
    value: tmbParameterValue.value as string,
    type: tmbParameterValue.type,
    parameter: mapParameter(tmbParameterValue.parameter) as EnumParameter,
  };
}

export function mapTextParameterValue(tmbParameterValue: TmbParameterValue): TextParameterValue {
  return {
    value: tmbParameterValue.value as string,
    type: tmbParameterValue.type,
    parameter: mapParameter(tmbParameterValue.parameter),
  };
}

export function mapParameterValue(tmbParameterValue: TmbParameterValue): ParameterValue {
  if (tmbParameterValue.type === "NUMBER") {
    return mapNumberParameterValue(tmbParameterValue);
  }
  if (tmbParameterValue.type === "ENUM") {
    return mapEnumParameterValue(tmbParameterValue);
  }
  return mapTextParameterValue(tmbParameterValue);
}

export function mapActionValueTableRow(tmbActionValue: TmbActionValueRow): ActionValueTableRow {
  return {
    parameterValues: tmbActionValue.values.map((value) => mapParameterValue(value)),
  };
}

export function mapAction(action: TmbAction): Action {
  return {
    name: action.name,
    tmbId: action.id,
    parameters: action.uniqueParameters.map((p) => mapParameter(p)),
    rowParameters: action.rowParameters.map((p) => mapParameter(p)),
  };
}

export function mapActionValue(actionValue: TmbActionValue): ActionValue {
  return {
    action: mapAction(actionValue.action),
    parameterValues: actionValue.values.map((pv) => mapParameterValue(pv)),
    rows: actionValue.rows.map((pv) => mapActionValueTableRow(pv)),
  };
}

export function mapSubstrateClass(tmbSubstrateClass: TmbSubstrateClassBase): SubstrateClass {
  return {
    tmbId: tmbSubstrateClass.id,
    name: tmbSubstrateClass.name,
    types: tmbSubstrateClass.types,
    parameters: [
      ...tmbSubstrateClass.inheritedParameterValues.map((p) => mapParameter(p.parameter)),
      ...tmbSubstrateClass.parameterValues.map((p) => mapParameter(p.parameter)),
    ],
  };
}

export function mapSubstrateClassTree(tmbSubstrateClass: TmbSubstrateClass): Tree<SubstrateClass> {
  return {
    id: tmbSubstrateClass.id,
    name: tmbSubstrateClass.name,
    data: mapSubstrateClass(tmbSubstrateClass),
    children: tmbSubstrateClass.children.map((sc) => mapSubstrateClassTree(sc)),
  };
}

export function mapTestSpecificationReference(
  tmbTestPlanVersionResult: TmbTestPlanVersionResult,
): TestSpecificationReference {
  return {
    id: tmbTestPlanVersionResult.testMethodId,
    name: tmbTestPlanVersionResult.testMethodName,
    version: tmbTestPlanVersionResult.testMethodVersion,
  };
}

export function mapTestSpecificationLifecycleReference(tmbTestPlan: TmbTestPlan): TestSpecificationLifecycleReference {
  const { id, name, versionStatuses } = tmbTestPlan;
  return {
    id,
    name,
    lifeCycleStateByVersion: versionStatuses.reduce<LifeCycleStateByVersion>((stateByVersion, currStatus) => {
      stateByVersion[String(currStatus.version)] = currStatus.lifeCycleState;
      return stateByVersion;
    }, {} as LifeCycleStateByVersion),
  };
}

export type TestMethod = {
  id: number;
  version: number;
  name: string;
  template: boolean;
};

export type TestSpecificationReference = {
  id: number;
  name: string;
  version: number;
};

class TesaTmbApi extends Api {
  constructor(dialogContext: ConfirmationDialogContextType) {
    const envObject = localStorage.getItem("envObject");
    const altBaseUrl: string = JSON.parse(envObject || "{}").tmb_server;
    super(dialogContext, { baseUrl: altBaseUrl });
  }

  public fetchActions(
    section: "substrate" | "material" | "testpiece",
    flowType?: "PREPARATION" | "TREATMENT",
  ): Promise<AxiosResponse<PagedResponse<TmbAction>>> {
    const flowTypeParam = `&flowType=${flowType}`;
    return this.get(`/tmb/actions?pageIndex=0&pageSize=100&section=${section}${flowType ? flowTypeParam : ""}`);
  }

  public fetchCustomerPartTypes(searchTerm: string): Promise<
    AxiosResponse<
      PagedResponse<{
        id: number;
        name: string;
      }>
    >
  > {
    return this.get(`/tmb/customer-parts?sortBy=NAME&pageSize=100&term=${searchTerm}`);
  }

  public fetchActionTemplates(tmbId: number): Promise<ActionTemplate[]> {
    return this.get(`/tmb/action-templates?pageSize=100&sortBy=NAME&actionId=${tmbId}`).then(
      (res: AxiosResponse<PagedResponse<TmbActionTemplate>>) => {
        const tmbActionTemplates: TmbActionTemplate[] = res.data.data;
        return tmbActionTemplates.map((tat) => ({
          id: tat.id,
          name: tat.name,
          value: mapActionValue(tat.actionValue),
        }));
      },
    );
  }

  public fetchParameters(section: "condition" | "measurement"): Promise<AxiosResponse<PagedResponse<TmbParameter>>> {
    return this.get(`/tmb/parameters?pageIndex=0&pageSize=999&section=${section}`);
  }

  public fetchSubstrateParameters(): Promise<AxiosResponse<PagedResponse<TmbParameter>>> {
    return this.get("/tmb/parameters?pageIndex=0&pageSize=999&domain=substrate");
  }

  public fetchSubstrateRoots(): Promise<AxiosResponse<TmbSubstrateClassBase[]>> {
    return this.get("/tmb/substrate-classes/root");
  }

  public fetchSubstrateTree(nodeId: number, tags: string[]): Promise<AxiosResponse<TmbSubstrateClass>> {
    const query = tags.map((tag) => `categories=${tag}`).join("&");
    const queryPath = query ? `?${query}` : "";
    return this.get(`/tmb/substrate-class/${nodeId}/tree${queryPath}`);
  }

  public fetchTestMethodVersions(term: string): ApiResponse<PagedResponse<TestMethod>> {
    return this.get(`/tmb/test-methods/search?term=${encodeURIComponent(term)}&pageIndex=0&pageSize=50&states=PUBLISHED
    `);
  }

  public fetchTestPlanVersion(testSeriesId: number, testSeriesVersion: number): ApiResponse<TmbTestPlanVersion> {
    return this.get(`/tmb/test-plan/${testSeriesId}/version/${testSeriesVersion}`);
  }

  public searchTestSpecificationVersions(
    specificationName: string,
    pageSize?: number,
  ): Promise<PagedResponse<TestSpecificationReference>> {
    const pageSizeUrl = `&pageSize=${pageSize}`;
    const query = `searchString=${specificationName}${pageSize ? pageSizeUrl : ""}`;
    return this.get(`tmb/test-plans/versions/publishedByName?${query}`).then((res) => ({
      ...res.data,
      data: res.data.data.map((testPlanVersionResult: TmbTestPlanVersionResult) =>
        mapTestSpecificationReference(testPlanVersionResult),
      ),
    }));
  }

  public searchTestSpecificationStatus(
    ids: number[],
    name: string = "",
    pageIndex: number = 0,
    pageSize: number = 20,
  ): Promise<PagedResponse<TestSpecificationLifecycleReference>> {
    const query = new URLSearchParams({
      name,
      pageIndex: String(pageIndex),
      pageSize: String(pageSize),
    });
    ids.forEach((id) => query.append("ids", String(id)));

    return this.get(`tmb/test-plans?${query}`).then((res: AxiosResponse<PagedResponse<TmbTestPlan>>) => ({
      ...res.data,
      data: res.data.data.map<TestSpecificationLifecycleReference>((tmbTestPlan: TmbTestPlan) =>
        mapTestSpecificationLifecycleReference(tmbTestPlan),
      ),
    }));
  }
}

export default TesaTmbApi;
